import { readdir, readFile, stat } from "node:fs/promises";
import { extname, join } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type {
  RunbookEntry,
  RunbookFilter,
  RunbookFrontmatter,
} from "./types.js";

const RESERVED_FILES: ReadonlySet<string> = new Set(["README.md", "_INDEX.md"]);

/**
 * Directory-backed runbook store. Expected layout is flat —
 * `<rootDir>/<slug>.md`, no subdirectories.
 *
 * Non-entry files at the root (README, _INDEX, anything not ending in `.md`)
 * are silently ignored. A missing root directory returns an empty list
 * rather than throwing.
 */
export class RunbookStore {
  constructor(readonly rootDir: string) {}

  /** All entries, sorted by slug ascending. */
  async list(): Promise<readonly RunbookEntry[]> {
    let names: string[];
    try {
      names = await readdir(this.rootDir);
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
    const out: RunbookEntry[] = [];
    for (const name of names) {
      if (extname(name) !== ".md") continue;
      if (RESERVED_FILES.has(name)) continue;
      const path = join(this.rootDir, name);
      try {
        const info = await stat(path);
        if (!info.isFile()) continue;
      } catch {
        continue;
      }
      const raw = await readFile(path, "utf8");
      const { frontmatter, body } =
        parseFrontmatter<RunbookFrontmatter>(raw);
      const slug = name.slice(0, -3);
      out.push({ slug, path, frontmatter, body });
    }
    return out.sort((a, b) => a.slug.localeCompare(b.slug));
  }

  /** A single entry by slug, or undefined if not found. */
  async get(slug: string): Promise<RunbookEntry | undefined> {
    const all = await this.list();
    return all.find((e) => e.slug === slug);
  }

  /**
   * Subset of entries matching the given criteria. Empty/undefined fields
   * are ignored. Returns entries in the same order as {@link list}.
   */
  async filter(criteria: RunbookFilter): Promise<readonly RunbookEntry[]> {
    const all = await this.list();
    return all.filter((e) => {
      if (criteria.status !== undefined && e.frontmatter.status !== criteria.status) {
        return false;
      }
      if (criteria.tag !== undefined) {
        const tags = e.frontmatter.tags ?? [];
        if (!tags.includes(criteria.tag)) return false;
      }
      if (
        criteria.incidentType !== undefined &&
        e.frontmatter["incident-type"] !== criteria.incidentType
      ) {
        return false;
      }
      return true;
    });
  }

  /**
   * Entries whose `last_tested` is older than `staleAfterDays` from the
   * given reference date (default: today). Entries without a `last_tested`
   * field are treated as "never tested" and are always returned.
   *
   * This is the practical reason runbooks are a module rather than plain
   * data — periodic reverification is a real operational need.
   */
  async getStaleByLastTested(
    staleAfterDays: number,
    referenceDate?: Date,
  ): Promise<readonly RunbookEntry[]> {
    if (!Number.isFinite(staleAfterDays) || staleAfterDays < 0) {
      throw new Error(`Invalid staleAfterDays: ${staleAfterDays}`);
    }
    const now = referenceDate ?? new Date();
    const threshold = now.getTime() - staleAfterDays * 86_400_000;
    const all = await this.list();
    return all.filter((e) => {
      const lt = e.frontmatter.last_tested;
      if (!lt) return true;
      const parsed = parseIsoDate(lt);
      if (parsed === undefined) return true;
      return parsed < threshold;
    });
  }
}

function parseIsoDate(s: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
  const t = Date.parse(`${s}T00:00:00Z`);
  return Number.isNaN(t) ? undefined : t;
}
